<template>
  <div v-if="visibleFields.length" class="w-full flex flex-col gap-6">
    <div v-for="field in visibleFields" :key="field.name" class="w-full flex flex-col gap-2">
      <div class="w-full">
        <!-- Our label, not the design system's (14px semibold), so it matches the
             16px medium labels of the fields around it. -->
        <label
          :for="field.name"
          class="text-base text-primary font-inter font-medium block mb-1 leading-5"
          >{{ field.label }}</label
        >
        <DsSearchableDropdown
          :id="field.name"
          :name="field.name"
          :options="field.options"
          :model-value="modelValue[field.name] || undefined"
          :placeholder="careerFieldOptions ? 'Select an option' : 'Loading options…'"
          :help-text="field.helpText"
          search-placeholder="Search"
          @update:model-value="value => update(field.name, value)"
        />
      </div>
      <DsInput
        v-if="field.isOtherChosen"
        :id="field.otherName"
        :name="field.otherName"
        :model-value="modelValue[field.otherName] || ''"
        :max-length="OTHER_CAREER_FIELD_MAX_LENGTH"
        placeholder="Please specify"
        @update:model-value="value => update(field.otherName, value)"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import {
  Input as DsInput,
  SearchableDropdown as DsSearchableDropdown,
} from '@team-hogc/hogc-one-design-system';
import {
  OTHER_CAREER_FIELD_MAX_LENGTH,
  careerFieldsForStage,
} from '@/constants/profile/careerFields';
import { othersLast } from '@/utils/others-last';

const FIELDS = [
  {
    name: 'fieldOfStudyId',
    otherName: 'otherFieldOfStudy',
    label: 'Field of study',
    list: 'fieldsOfStudy',
  },
  // The two lists overlap by name ("Banking, Finance & Insurance" is an industry,
  // "Accounting, Audit & Finance" a profession), so each says which question it asks.
  {
    name: 'industryId',
    otherName: 'otherIndustry',
    label: 'Industry',
    helpText: "Your organisation's industry",
    list: 'industries',
  },
  {
    name: 'professionId',
    otherName: 'otherProfession',
    label: 'Profession',
    helpText: 'Your primary job function',
    list: 'professions',
  },
];

/**
 * Industry and profession (when Working) and field of study (when Studying at
 * a polytechnic, ITE, university or post-graduate level), all optional.
 *
 * `modelValue` holds all six keys (each option id plus its `other*` text). A
 * field that does not apply is hidden, not cleared, so moving between stages
 * loses nothing. The caller decides whether to render this at all (the
 * `profile-career-fields` flag).
 *
 * The options come from the API. A hidden (`isActive: false`) option is offered
 * to no one, but still shown to a member who already chose it.
 */
export default {
  name: 'CareerFields',
  components: { DsInput, DsSearchableDropdown },
  props: {
    modelValue: { type: Object, required: true },
    stageOfLife: { type: String, default: null },
    /** The member's level of study (`levelrank` id); only read when Studying. */
    levelId: { type: [Number, String], default: null },
  },
  emits: ['update:modelValue'],
  data() {
    return { OTHER_CAREER_FIELD_MAX_LENGTH };
  },
  computed: {
    ...mapState('lookups', ['careerFieldOptions']),
    visibleFields() {
      const shown = careerFieldsForStage(this.stageOfLife, this.levelId);
      return FIELDS.filter(field => shown[field.name]).map(field => {
        const selected = this.modelValue[field.name];
        const all = this.careerFieldOptions?.[field.list] ?? [];
        return {
          ...field,
          options: othersLast(
            all.filter(option => option.isActive || option.id === selected),
            { labelOf: option => option.name, isOther: option => option.isOther },
          ).map(option => ({ value: option.id, label: option.name })),
          isOtherChosen: all.some(option => option.id === selected && option.isOther),
        };
      });
    },
  },
  mounted() {
    this.fetchCareerFieldOptions();
  },
  methods: {
    ...mapActions('lookups', ['fetchCareerFieldOptions']),
    update(key, value) {
      this.$emit('update:modelValue', { ...this.modelValue, [key]: value || null });
    },
  },
};
</script>
